/**
 * @file Bounded standalone Compliance board rendering and interaction owner.
 */

import {formatDate} from "../../shared/utils/dateUtils.js";
import {getUserDisplayNameById} from "../../shared/utils/userDisplay.js";
import {openConfirmationModal} from "../../shared/ui/confirmationModal.js";
import {escapeHTML} from "../../shared/utils/contentUtils.js";
import {state as appState} from "../../app/state/state.js";
import {canAccessView} from "../../app/routing/boardAccess.js";
import {
    changeComplianceRequirementLifecycle,
    changeComplianceTrackedItemLifecycle,
} from "./complianceApi.js";
import {
    confirmComplianceRecordDelete,
    openComplianceItemModal,
    openComplianceRecordModal,
    openComplianceRequirementModal,
} from "./complianceModals.js";
import {openComplianceEvidenceModal} from "./complianceEvidence.js";
import {openComplianceReportModal} from "./complianceReports.js";
import {
    getComplianceHistoryState,
    getComplianceRequirementState,
    getComplianceState,
    invalidateComplianceHistory,
    isComplianceGenerationCurrent,
    loadComplianceHistoryPage,
    loadComplianceRequirementPage,
    loadComplianceSummaryPage,
    reconcileComplianceItem,
    reconcileComplianceRequirement,
} from "./complianceState.js";

const EMPTY_USED_ITEM_CHOICE_REQUIRED =
    "empty_used_item_choice_required";

function focusKey(parts) {
    return parts.filter(Boolean).join(":");
}

function getDueStateLabel(state) {
    return {
        overdue: "Overdue",
        due_now: "Due now",
        due_soon: "Due soon",
        normal: "Current",
        no_current: "No current Compliance record",
    }[state] || "No current Compliance record";
}

function getDueStateClasses(state) {
    return {
        overdue: "border-red-200 bg-red-50 text-red-700",
        due_now: "border-orange-200 bg-orange-50 text-orange-700",
        due_soon: "border-amber-200 bg-amber-50 text-amber-700",
        normal: "border-emerald-200 bg-emerald-50 text-emerald-700",
        no_current: "border-gray-200 bg-gray-50 text-gray-600",
    }[state] || "border-gray-200 bg-gray-50 text-gray-600";
}

function renderDueBadge(state) {
    return `
        <span class="inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${getDueStateClasses(state)}">
            ${escapeHTML(getDueStateLabel(state))}
        </span>
    `;
}

/**
 * Returns a read-only tracked-item source label.
 * @param {object} item Tracked item.
 * @returns {string} Source label.
 */
function getTrackedItemSourceLabel(item) {
    return {
        user: "Linked user",
        asset: "Linked asset",
        manual: "Manual entry",
    }[item?.sourceType] || "System linked";
}

/**
 * Returns a read-only tracked-item lifecycle label.
 * @param {object} item Tracked item.
 * @returns {string} Lifecycle label.
 */
function getTrackedItemLifecycleLabel(item) {
    if (item?.sourceState === "deleted") {return "Deleted source";}
    if (item?.sourceState === "inactive") {return "Inactive";}
    if (item?.sourceState === "closed") {return "Closed";}
    return item?.isActive === false ? "Not active" : "Active";
}

function getTrackedItemLifecycleClasses(item) {
    return item?.isActive === false ?
        "border-gray-300 bg-gray-100 text-gray-700" :
        "border-gray-200 bg-white text-gray-600";
}

function renderPageError(message) {
    return `
        <p role="alert" class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            ${escapeHTML(message)}
        </p>
    `;
}

function getVisibleItems(routeState) {
    return [
        ...routeState.summaries.active.entries,
        ...(routeState.showInactive ?
            routeState.summaries.inactive.entries :
            []),
    ];
}

function getCurrentRecord(itemId, requirement) {
    const history = getComplianceHistoryState(itemId, requirement.id);
    return history?.entries.find((record) =>
        record.id === requirement.currentRecordId &&
        record.isCurrent === true,
    ) || null;
}

function renderRecord(record, item, requirement) {
    const current = record.isCurrent === true;
    const creator = record.createdBy ?
        getUserDisplayNameById(record.createdBy) :
        "Unknown";
    const superseded = record.supersededByRecordId ?
        `Superseded ${formatDate(record.supersededAt)} by ` +
            getUserDisplayNameById(record.supersededBy) :
        "";
    return `
        <li class="rounded-md border ${current ? "border-emerald-200 bg-emerald-50/40" : "border-gray-200 bg-white"} p-3"
            data-compliance-record-id="${escapeHTML(record.id)}">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                        <span class="font-medium text-gray-800">${escapeHTML(record.startDate || "No start date")}</span>
                        ${current ? '<span class="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Current</span>' : ""}
                    </div>
                    <p class="mt-1 text-sm text-gray-600">
                        ${record.frequencyMonths ?
                            `Every ${escapeHTML(String(record.frequencyMonths))} month${Number(record.frequencyMonths) === 1 ? "" : "s"}` :
                            "Manual next due"}
                        · Next due ${escapeHTML(record.nextDueDate || "not set")}
                    </p>
                    <p class="mt-1 text-xs text-gray-500">
                        Recorded by ${escapeHTML(creator)} on ${escapeHTML(formatDate(record.createdAt) || "unknown date")}
                        ${superseded ? ` · ${escapeHTML(superseded)}` : ""}
                    </p>
                </div>
                ${record.sourceType === "manual" ? `
                    <div class="flex flex-wrap gap-2">
                        <button type="button" class="text-sm font-medium text-[var(--dfc-brand-accent)] hover:underline"
                            data-compliance-evidence
                            data-compliance-focus-key="${escapeHTML(focusKey(["record", item.id, requirement.id, record.id, "evidence"]))}">
                            Evidence
                        </button>
                        ${current ? `<button type="button" class="text-sm font-medium text-gray-700 hover:text-gray-900"
                            data-compliance-correct-record
                            data-compliance-focus-key="${escapeHTML(focusKey(["record", item.id, requirement.id, record.id, "correct"]))}">
                            Correct
                        </button>
                        <button type="button" class="text-sm font-medium text-red-600 hover:text-red-700"
                            data-compliance-delete-record
                            data-compliance-focus-key="${escapeHTML(focusKey(["record", item.id, requirement.id, record.id, "delete"]))}">
                            Delete
                        </button>
                        ` : ""}
                    </div>
                ` : ""}
            </div>
        </li>
    `;
}

function renderServiceRecord(record, item, requirement) {
    const creator = record.createdBy === "system" ?
        "System" :
        getUserDisplayNameById(record.createdBy) || "Unknown";
    const canOpenMaintenance = canAccessView(
        appState.currentUserProfile,
        "maintenance",
    );
    return `
        <li class="rounded-md border ${record.isCurrent ? "border-emerald-200 bg-emerald-50/40" : "border-gray-200 bg-white"} p-3"
            data-compliance-service-id="${escapeHTML(record.id)}">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                        <span class="font-medium text-gray-800">Performed ${escapeHTML(record.startDate || "date unavailable")}</span>
                        ${record.isCurrent ? '<span class="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Current</span>' : ""}
                    </div>
                    <p class="mt-1 text-xs text-gray-500">
                        Recorded by ${escapeHTML(creator)} on ${escapeHTML(formatDate(record.createdAt) || "unknown date")}
                    </p>
                </div>
                <div class="flex items-center gap-2">
                    <button type="button"
                        data-compliance-service-evidence
                        class="text-sm font-semibold text-[var(--dfc-brand-accent)] hover:underline"
                        data-compliance-focus-key="${escapeHTML(focusKey(["service", item.id, requirement.id, record.id, "evidence"]))}">
                        Evidence
                    </button>
                    ${canOpenMaintenance ? `
                        <button type="button"
                            data-compliance-open-maintenance
                            class="standard-action-icon-btn"
                            title="Open Maintenance Service"
                            aria-label="Open Maintenance Service"
                            data-compliance-focus-key="${escapeHTML(focusKey(["service", item.id, requirement.id, record.id, "open"]))}">
                            <span class="material-symbols-outlined text-[19px] leading-none" aria-hidden="true">open_in_new</span>
                        </button>
                    ` : ""}
                </div>
            </div>
        </li>
    `;
}

function renderHistory(item, requirement) {
    const history = getComplianceHistoryState(item.id, requirement.id);
    const sourceHistory = requirement.sourceType === "maintenance_service";
    if (!history) {
        return '<p class="mt-3 text-sm text-gray-500">Loading record history...</p>';
    }
    const summary = history.sourceSummary;
    return `
        <div class="mt-3 border-t border-gray-200 pt-3">
            <h5 class="text-sm font-semibold text-gray-700">${sourceHistory ? "Maintenance Service history" : "Compliance record history"}</h5>
            ${sourceHistory && summary ? `
                <p class="mt-1 text-xs text-gray-500">
                    Service frequency ${summary.frequencyMonths ?
                        `${escapeHTML(String(summary.frequencyMonths))} month${Number(summary.frequencyMonths) === 1 ? "" : "s"}` :
                        "not set"}
                    · Last service ${escapeHTML(summary.lastServiceDate || "not recorded")}
                    · Next due ${escapeHTML(summary.nextServiceDue || "not set")}
                </p>
            ` : ""}
            ${history.error ?
                renderPageError(`${sourceHistory ? "Service" : "Record"} history could not be loaded. Try again.`) :
                ""}
            ${history.entries.length ? `
                <ol class="mt-2 space-y-2">
                    ${history.entries.map((record) =>
                        sourceHistory ?
                            renderServiceRecord(record, item, requirement) :
                            renderRecord(record, item, requirement),
                    ).join("")}
                </ol>
            ` : history.loading ?
                '<p class="mt-2 text-sm text-gray-500">Loading history...</p>' :
                `<p class="mt-2 text-sm text-gray-500">${sourceHistory ? "No qualifying Maintenance Services yet." : "No Compliance records yet."}</p>`}
            ${history.hasMore ? `
                <button type="button" data-compliance-load-history
                    class="mt-3 text-sm font-semibold text-[var(--dfc-brand-accent)] disabled:opacity-50"
                    ${history.loading ? "disabled" : ""}
                    data-compliance-focus-key="${escapeHTML(focusKey(["history", item.id, requirement.id]))}">
                    ${history.loading ?
                        "Loading..." :
                        history.error ? "Retry history" : "Load more history"}
                </button>
            ` : ""}
        </div>
    `;
}

function renderRequirement(item, requirement, routeState) {
    const key = focusKey([item.id, requirement.id]);
    const expanded = routeState.expandedRequirements.has(key);
    const noCurrent = requirement.currentDueState === "no_current";
    const recordedBy = requirement.currentRecordedBy ?
        getUserDisplayNameById(requirement.currentRecordedBy) :
        "Unknown";
    const recordedAt = formatDate(requirement.currentRecordedAt) ||
        "unknown date";
    return `
        <li class="rounded-lg border border-gray-200 bg-gray-50/60 p-3"
            data-compliance-requirement-id="${escapeHTML(requirement.id)}">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <button type="button" data-compliance-toggle-requirement
                    class="min-w-0 flex-1 text-left"
                    aria-expanded="${expanded}"
                    data-compliance-focus-key="${escapeHTML(focusKey(["requirement", item.id, requirement.id]))}">
                    <span class="flex items-start gap-2">
                        <span class="material-symbols-outlined mt-0.5 text-lg text-gray-500" aria-hidden="true">
                            ${expanded ? "expand_less" : "expand_more"}
                        </span>
                        <span class="min-w-0">
                            <span class="block font-medium text-gray-800">${escapeHTML(requirement.name || "Compliance requirement")}</span>
                            <span class="mt-1 flex flex-wrap items-center gap-2">
                                ${renderDueBadge(requirement.currentDueState)}
                                ${noCurrent ? "" : `<span class="text-xs text-gray-500">Started ${escapeHTML(requirement.currentStartDate || "unknown")} · Next due ${escapeHTML(requirement.currentNextDueDate || "not set")} · Recorded by ${escapeHTML(recordedBy)} on ${escapeHTML(recordedAt)}</span>`}
                            </span>
                        </span>
                    </span>
                </button>
                <div class="flex flex-wrap items-center gap-2 pl-7 sm:pl-0">
                    ${requirement.currentRecordSource ===
                        "maintenance_service" &&
                        requirement.currentRecordId ? `
                        <button type="button"
                            data-compliance-derived-evidence
                            class="text-sm font-semibold text-[var(--dfc-brand-accent)] hover:underline">
                            Evidence
                        </button>
                    ` : ""}
                    ${requirement.sourceType === "manual" &&
                        requirement.isActive !== false &&
                        item.isActive !== false ? `
                        <button type="button" data-compliance-record
                            class="rounded-md bg-[var(--dfc-brand-primary)] px-3 py-1.5 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)]"
                            data-compliance-focus-key="${escapeHTML(focusKey(["requirement", item.id, requirement.id, "record"]))}">
                            Record compliance
                        </button>
                    ` : ""}
                    ${requirement.sourceType === "manual" ? `
                        <button type="button" data-compliance-requirement-action="${requirement.isActive === false ? "reopen" : "close"}"
                            class="text-sm font-medium text-gray-600 hover:text-gray-900"
                            data-compliance-focus-key="${escapeHTML(focusKey(["requirement", item.id, requirement.id, "lifecycle"]))}">
                            ${requirement.isActive === false ? "Reopen" : "Close"}
                        </button>
                        ${requirement.hasRecords === false ? `
                            <button type="button" data-compliance-requirement-action="delete"
                                class="text-sm font-medium text-red-600 hover:text-red-700"
                                data-compliance-focus-key="${escapeHTML(focusKey(["requirement", item.id, requirement.id, "delete"]))}">
                                Delete
                            </button>
                        ` : ""}
                    ` : ""}
                </div>
            </div>
            ${expanded ? renderHistory(item, requirement) : ""}
        </li>
    `;
}

function renderRequirements(item, routeState) {
    const requirements = getComplianceRequirementState(item.id);
    if (!requirements) {
        return '<p class="mt-3 text-sm text-gray-500">Loading requirements...</p>';
    }
    const entries = [
        ...requirements.active.entries,
        ...(routeState.showInactive ? requirements.inactive.entries : []),
    ];
    return `
        <div class="mt-4 border-t border-gray-200 pt-4">
            <div class="flex flex-wrap items-center justify-between gap-2">
                <h4 class="font-semibold text-gray-800">Compliance requirements</h4>
                ${item.isActive !== false ? `
                    <button type="button" data-compliance-add-requirement
                        class="text-sm font-semibold text-[var(--dfc-brand-accent)]"
                        data-compliance-focus-key="${escapeHTML(focusKey(["item", item.id, "add-requirement"]))}">
                        + Add requirement
                    </button>
                ` : ""}
            </div>
            ${requirements.active.error ||
                (routeState.showInactive && requirements.inactive.error) ?
                renderPageError("Requirements could not be loaded. Try again.") :
                ""}
            ${entries.length ? `
                <ul class="mt-3 space-y-3">
                    ${entries.map((requirement) =>
                        renderRequirement(item, requirement, routeState),
                    ).join("")}
                </ul>
            ` : requirements.active.loading ||
                (routeState.showInactive && requirements.inactive.loading) ?
                '<p class="mt-3 text-sm text-gray-500">Loading requirements...</p>' :
                '<p class="mt-3 rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500">No compliance requirements yet.</p>'}
            ${requirements.active.hasMore ||
                (routeState.showInactive && requirements.inactive.hasMore) ? `
                <button type="button" data-compliance-load-requirements
                    class="mt-3 text-sm font-semibold text-[var(--dfc-brand-accent)]"
                    data-compliance-focus-key="${escapeHTML(focusKey(["requirements", item.id]))}">
                    Load more requirements
                </button>
            ` : ""}
        </div>
    `;
}

function renderItem(item, routeState) {
    const expanded = routeState.expandedItems.has(item.id);
    const manual = item.sourceType === "manual";
    const lifecycleClasses = item.isActive === false ?
        "border-gray-300 border-l-4 border-l-gray-400 bg-gray-50" :
        "border-gray-200 bg-white";
    return `
        <article class="rounded-lg border p-4 shadow-sm ${lifecycleClasses}"
            data-compliance-item-id="${escapeHTML(item.id)}">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <button type="button" data-compliance-toggle-item
                    class="min-w-0 flex-1 text-left"
                    aria-expanded="${expanded}"
                    data-compliance-focus-key="${escapeHTML(focusKey(["item", item.id]))}">
                    <span class="flex items-start gap-2">
                        <span class="material-symbols-outlined mt-0.5 text-lg text-gray-500" aria-hidden="true">
                            ${expanded ? "expand_less" : "expand_more"}
                        </span>
                        <span class="min-w-0">
                            <span class="block font-semibold text-gray-900">${escapeHTML(item.name || "Tracked item")}</span>
                            <span class="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500"
                                aria-label="Tracked item source and lifecycle">
                                <span class="rounded-full border border-gray-200 bg-white px-2 py-0.5">
                                    ${escapeHTML(getTrackedItemSourceLabel(item))}
                                </span>
                                <span class="rounded-full border px-2 py-0.5 ${getTrackedItemLifecycleClasses(item)}">
                                    ${escapeHTML(getTrackedItemLifecycleLabel(item))}
                                </span>
                                ${item.sourceType === "user" ? `
                                    <span>People category · source managed</span>
                                ` : ""}
                            </span>
                            <span class="mt-1 flex flex-wrap items-center gap-2">
                                ${renderDueBadge(item.summaryDueState)}
                                <span class="text-xs text-gray-500">
                                    ${escapeHTML(item.summaryRequirementName || "No requirement summary")}
                                    ${item.summaryDueDate ? ` · ${escapeHTML(item.summaryDueDate)}` : ""}
                                </span>
                            </span>
                        </span>
                    </span>
                </button>
                <div class="flex flex-wrap gap-2 pl-7 sm:pl-0">
                    ${manual ? `
                        <button type="button" data-compliance-edit-item
                            class="text-sm font-medium text-gray-600 hover:text-gray-900"
                            data-compliance-focus-key="${escapeHTML(focusKey(["item", item.id, "edit"]))}">
                            Edit
                        </button>
                        <button type="button" data-compliance-item-action="${item.isActive === false ? "restore" : "archive"}"
                            class="text-sm font-medium text-gray-600 hover:text-gray-900"
                            data-compliance-focus-key="${escapeHTML(focusKey(["item", item.id, "lifecycle"]))}">
                            ${item.isActive === false ? "Restore" : "Archive"}
                        </button>
                        ${item.hasRequirements === false ? `
                            <button type="button" data-compliance-item-action="delete"
                                class="text-sm font-medium text-red-600 hover:text-red-700"
                                data-compliance-focus-key="${escapeHTML(focusKey(["item", item.id, "delete"]))}">
                                Delete
                            </button>
                        ` : ""}
                    ` : ""}
                </div>
            </div>
            ${expanded ? renderRequirements(item, routeState) : ""}
        </article>
    `;
}

function renderCategory(category, items, routeState) {
    const applicablePageHasMore = routeState.summaries.active.hasMore ||
        (routeState.showInactive &&
            routeState.summaries.inactive.hasMore);
    return `
        <section class="rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4"
            data-compliance-category-id="${escapeHTML(category.id)}">
            <div class="mb-3 flex items-center justify-between gap-3">
                <h3 class="text-lg font-semibold text-gray-900">${escapeHTML(category.name || "Compliance category")}</h3>
                <span class="text-sm text-gray-500">${items.length} loaded tracked item${items.length === 1 ? "" : "s"}</span>
            </div>
            ${items.length ? `
                <div class="space-y-3">
                    ${items.map((item) => renderItem(item, getComplianceState())).join("")}
                </div>
            ` : `
                <p class="rounded-lg border border-dashed border-gray-300 bg-white p-5 text-sm text-gray-500">
                    ${applicablePageHasMore ?
                        "No tracked items loaded in this category yet." :
                        "No tracked items in this category."}
                </p>
            `}
        </section>
    `;
}

function restoreFocus(container) {
    const key = getComplianceState().focusKey;
    if (!key) {return;}
    requestAnimationFrame(() => {
        container.querySelector(
            `[data-compliance-focus-key="${CSS.escape(key)}"]`,
        )?.focus({preventScroll: true});
    });
}

function setActionFocus(element) {
    const key = element?.dataset?.complianceFocusKey;
    if (key) {getComplianceState().focusKey = key;}
}

function showLifecycleFailure(error) {
    const reason = error?.details?.reason || error?.data?.reason || "";
    const messages = {
        tracked_item_has_records:
            "Tracked items with Compliance records cannot be deleted.",
        tracked_item_has_requirements:
            "Delete every empty requirement shell before deleting this tracked item.",
        requirement_has_records:
            "Requirements with retained Compliance history cannot be deleted.",
        stale_expected_state:
            "This Compliance data changed. Reload the board before trying again.",
    };
    openConfirmationModal(
        "Compliance lifecycle",
        escapeHTML(messages[reason] || error?.message ||
            "The lifecycle change could not be completed."),
        () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

async function applyItemLifecycle(container, generation, item, action, options = {}) {
    try {
        const response = await changeComplianceTrackedItemLifecycle({
            itemId: item.id,
            expectedUpdatedAt: item.updatedAt,
            action,
            ...(options.confirmEmptyArchive ?
                {confirmEmptyArchive: true} :
                {}),
        });
        if (!isComplianceGenerationCurrent(generation)) {return;}
        if (response.outcome === EMPTY_USED_ITEM_CHOICE_REQUIRED) {
            openConfirmationModal(
                "Empty tracked item",
                `<strong>${escapeHTML(item.name)}</strong> has no requirements. Delete it permanently or archive it for later use?`,
                () => applyItemLifecycle(
                    container,
                    generation,
                    item,
                    "archive",
                    {confirmEmptyArchive: true},
                ),
                false,
                "Archive",
                null,
                "Archiving...",
                "Cancel",
                {
                    closeOnError: false,
                    leftAction: {
                        buttonText: "Delete item",
                        loadingText: "Deleting...",
                        onClick: () => applyItemLifecycle(
                            container,
                            generation,
                            item,
                            "delete",
                        ),
                        placement: "beforeConfirm",
                        variant: "dangerSecondary",
                    },
                },
            );
            return;
        }
        reconcileComplianceItem(response.item, item.id);
        const routeState = getComplianceState();
        routeState.focusKey = response.item &&
            (response.item.isActive !== false || routeState.showInactive) ?
            focusKey(["item", response.item.id, "lifecycle"]) :
            "add-item";
        renderComplianceBoard(container, generation);
    } catch (error) {
        setTimeout(() => showLifecycleFailure(error), 0);
        throw error;
    }
}

function confirmItemLifecycle(container, generation, item, action) {
    const labels = {
        archive: ["Archive tracked item?", "Archive", "Archiving..."],
        restore: ["Restore tracked item?", "Restore", "Restoring..."],
        delete: ["Delete tracked item?", "Delete item", "Deleting..."],
    };
    const selected = labels[action];
    if (!selected) {return;}
    openConfirmationModal(
        selected[0],
        `${selected[1]} <strong>${escapeHTML(item.name)}</strong>?`,
        () => applyItemLifecycle(container, generation, item, action),
        action === "delete",
        selected[1],
        null,
        selected[2],
    );
}

async function applyRequirementLifecycle(
    container,
    generation,
    item,
    requirement,
    action,
) {
    try {
        const response = await changeComplianceRequirementLifecycle({
            itemId: item.id,
            requirementId: requirement.id,
            expectedUpdatedAt: requirement.updatedAt,
            action,
        });
        if (!isComplianceGenerationCurrent(generation)) {return;}
        reconcileComplianceRequirement(
            item.id,
            response.requirement,
            response.item,
            requirement.id,
        );
        const routeState = getComplianceState();
        routeState.focusKey = response.requirement &&
            (response.requirement.isActive !== false ||
                routeState.showInactive) ?
            focusKey([
                "requirement",
                item.id,
                response.requirement.id,
                "lifecycle",
            ]) :
            focusKey(["item", item.id, "add-requirement"]);
        renderComplianceBoard(container, generation);
    } catch (error) {
        setTimeout(() => showLifecycleFailure(error), 0);
        throw error;
    }
}

function confirmRequirementLifecycle(
    container,
    generation,
    item,
    requirement,
    action,
) {
    const label = {
        close: "Close",
        reopen: "Reopen",
        delete: "Delete",
    }[action];
    openConfirmationModal(
        `${label} requirement?`,
        `${label} <strong>${escapeHTML(requirement.name)}</strong>?`,
        () => applyRequirementLifecycle(
            container,
            generation,
            item,
            requirement,
            action,
        ),
        action === "delete",
        label,
        null,
        `${label}...`,
    );
}

async function ensureRequirementHistory(
    container,
    generation,
    item,
    requirement,
) {
    const key = focusKey([item.id, requirement.id]);
    getComplianceState().expandedRequirements.add(key);
    const existingHistory = getComplianceHistoryState(
        item.id,
        requirement.id,
    );
    if (!existingHistory || existingHistory.error) {
        renderComplianceBoard(container, generation);
        await loadComplianceHistoryPage(
            generation,
            item.id,
            requirement.id,
            requirement.sourceType,
        );
    }
    if (isComplianceGenerationCurrent(generation)) {
        renderComplianceBoard(container, generation);
    }
    return getComplianceHistoryState(item.id, requirement.id);
}

async function loadCurrentRecordForCreate(
    container,
    generation,
    item,
    requirement,
) {
    const history = await ensureRequirementHistory(
        container,
        generation,
        item,
        requirement,
    );
    if (!requirement.currentRecordId) {
        return {ready: true, record: null};
    }
    if (!history || history.error) {
        return {ready: false, record: null};
    }
    const record = getCurrentRecord(item.id, requirement);
    if (record) {
        return {ready: true, record};
    }
    history.entries = [];
    history.cursor = null;
    history.hasMore = true;
    history.error = new Error(
        "The current Compliance record could not be loaded.",
    );
    renderComplianceBoard(container, generation);
    return {ready: false, record: null};
}

function findItem(container, element) {
    const itemId = element.closest("[data-compliance-item-id]")
        ?.dataset.complianceItemId;
    return getVisibleItems(getComplianceState())
        .find((entry) => entry.id === itemId) || null;
}

function findRequirement(item, element) {
    const requirementId = element
        .closest("[data-compliance-requirement-id]")
        ?.dataset.complianceRequirementId;
    const state = getComplianceRequirementState(item.id);
    return [
        ...(state?.active.entries || []),
        ...(state?.inactive.entries || []),
    ].find((entry) => entry.id === requirementId) || null;
}

function bindBoardInteractions(container, generation) {
    container.onclick = async (event) => {
        const control = event.target.closest("button");
        if (!control || !container.contains(control) ||
            !isComplianceGenerationCurrent(generation)) {return;}
        setActionFocus(control);
        const routeState = getComplianceState();

        if (control.matches("[data-compliance-add-item]")) {
            openComplianceItemModal({
                categories: routeState.categories,
                onSaved(response) {
                    reconcileComplianceItem(response.item);
                    routeState.focusKey = focusKey(["item", response.item.id]);
                    renderComplianceBoard(container, generation);
                },
            });
            return;
        }
        if (control.matches("[data-compliance-report]")) {
            openComplianceReportModal({
                categories: routeState.categories,
                format: control.dataset.complianceReport,
            });
            return;
        }
        if (control.matches("[data-compliance-show-inactive]")) {return;}
        if (control.matches("[data-compliance-load-summaries]")) {
            const nextView = routeState.summaries.active.hasMore ?
                "active" :
                "inactive";
            await loadComplianceSummaryPage(generation, nextView);
            renderComplianceBoard(container, generation);
            return;
        }

        const item = findItem(container, control);
        if (!item) {return;}
        if (control.matches("[data-compliance-toggle-item]")) {
            if (routeState.expandedItems.has(item.id)) {
                routeState.expandedItems.delete(item.id);
                renderComplianceBoard(container, generation);
                return;
            }
            routeState.expandedItems.add(item.id);
            renderComplianceBoard(container, generation);
            await loadComplianceRequirementPage(
                generation,
                item.id,
                "active",
            );
            if (routeState.showInactive) {
                await loadComplianceRequirementPage(
                    generation,
                    item.id,
                    "inactive",
                );
            }
            renderComplianceBoard(container, generation);
            return;
        }
        if (control.matches("[data-compliance-edit-item]")) {
            openComplianceItemModal({
                categories: routeState.categories,
                item,
                onSaved(response) {
                    reconcileComplianceItem(response.item);
                    renderComplianceBoard(container, generation);
                },
            });
            return;
        }
        if (control.matches("[data-compliance-item-action]")) {
            confirmItemLifecycle(
                container,
                generation,
                item,
                control.dataset.complianceItemAction,
            );
            return;
        }
        if (control.matches("[data-compliance-add-requirement]")) {
            openComplianceRequirementModal({
                item,
                onSaved(response) {
                    reconcileComplianceRequirement(
                        item.id,
                        response.requirement,
                        response.item,
                    );
                    routeState.focusKey = focusKey([
                        "requirement",
                        item.id,
                        response.requirement.id,
                    ]);
                    renderComplianceBoard(container, generation);
                },
            });
            return;
        }
        if (control.matches("[data-compliance-load-requirements]")) {
            const requirementState = getComplianceRequirementState(item.id);
            const nextView = requirementState?.active.hasMore ?
                "active" :
                "inactive";
            await loadComplianceRequirementPage(
                generation,
                item.id,
                nextView,
            );
            renderComplianceBoard(container, generation);
            return;
        }

        const requirement = findRequirement(item, control);
        if (!requirement) {return;}
        if (control.matches("[data-compliance-toggle-requirement]")) {
            const key = focusKey([item.id, requirement.id]);
            if (routeState.expandedRequirements.has(key)) {
                routeState.expandedRequirements.delete(key);
                renderComplianceBoard(container, generation);
                return;
            }
            await ensureRequirementHistory(
                container,
                generation,
                item,
                requirement,
            );
            return;
        }
        if (control.matches("[data-compliance-requirement-action]")) {
            confirmRequirementLifecycle(
                container,
                generation,
                item,
                requirement,
                control.dataset.complianceRequirementAction,
            );
            return;
        }
        if (control.matches("[data-compliance-derived-evidence]")) {
            await openComplianceEvidenceModal({
                item,
                requirement,
                record: {
                    id: requirement.currentRecordId,
                    startDate: requirement.currentStartDate,
                    sourceType: "maintenance_service",
                },
                onChanged(count) {
                    requirement.currentEvidenceCount = count;
                },
            });
            return;
        }
        if (control.matches("[data-compliance-record]")) {
            const current = await loadCurrentRecordForCreate(
                container,
                generation,
                item,
                requirement,
            );
            if (!current.ready) {return;}
            openComplianceRecordModal({
                item,
                requirement,
                currentRecord: current.record,
                onSaved(response) {
                    reconcileComplianceRequirement(
                        item.id,
                        response.requirement,
                        response.item,
                    );
                    invalidateComplianceHistory(item.id, requirement.id);
                    void ensureRequirementHistory(
                        container,
                        generation,
                        response.item,
                        response.requirement,
                    );
                },
            });
            return;
        }
        if (control.matches("[data-compliance-load-history]")) {
            await loadComplianceHistoryPage(
                generation,
                item.id,
                requirement.id,
                requirement.sourceType,
            );
            renderComplianceBoard(container, generation);
            return;
        }
        const serviceId = control.closest("[data-compliance-service-id]")
            ?.dataset.complianceServiceId;
        const service = getComplianceHistoryState(item.id, requirement.id)
            ?.entries.find((entry) => entry.id === serviceId);
        if (service &&
            control.matches("[data-compliance-service-evidence]")) {
            await openComplianceEvidenceModal({
                item,
                requirement,
                record: {
                    ...service,
                    sourceType: "maintenance_service",
                },
                onChanged(count) {
                    service.evidenceCount = count;
                    if (service.isCurrent) {
                        requirement.currentEvidenceCount = count;
                    }
                    renderComplianceBoard(container, generation);
                },
            });
            return;
        }
        if (service &&
            control.matches("[data-compliance-open-maintenance]")) {
            const {navigateToDetails} =
                await import("../../app/routing/navigationController.js");
            navigateToDetails(service.id, "maintenance");
            return;
        }
        const recordId = control.closest("[data-compliance-record-id]")
            ?.dataset.complianceRecordId;
        const record = getComplianceHistoryState(item.id, requirement.id)
            ?.entries.find((entry) => entry.id === recordId);
        if (!record) {return;}
        if (control.matches("[data-compliance-evidence]")) {
            await openComplianceEvidenceModal({
                item,
                requirement,
                record,
                onChanged(count) {
                    record.attachments = Array.from(
                        {length: count},
                        (_, index) => ({id: `evidence-${index}`}),
                    );
                    if (record.isCurrent) {
                        requirement.currentEvidenceCount = count;
                    }
                },
            });
            return;
        }
        if (control.matches("[data-compliance-correct-record]")) {
            openComplianceRecordModal({
                item,
                requirement,
                record,
                onSaved(response) {
                    reconcileComplianceRequirement(
                        item.id,
                        response.requirement,
                        response.item,
                    );
                    invalidateComplianceHistory(item.id, requirement.id);
                    void ensureRequirementHistory(
                        container,
                        generation,
                        response.item,
                        response.requirement,
                    );
                },
            });
            return;
        }
        if (control.matches("[data-compliance-delete-record]")) {
            confirmComplianceRecordDelete({
                item,
                requirement,
                record,
                onDeleted(response) {
                    routeState.focusKey = response.promotedRecordId ?
                        focusKey([
                            "record",
                            item.id,
                            response.requirement.id,
                            response.promotedRecordId,
                            "correct",
                        ]) :
                        response.requirement?.isActive !== false &&
                            response.item?.isActive !== false ?
                            focusKey([
                                "requirement",
                                item.id,
                                response.requirement.id,
                                "record",
                            ]) :
                            focusKey([
                                "requirement",
                                item.id,
                                response.requirement.id,
                            ]);
                    reconcileComplianceRequirement(
                        item.id,
                        response.requirement,
                        response.item,
                    );
                    invalidateComplianceHistory(item.id, requirement.id);
                    void ensureRequirementHistory(
                        container,
                        generation,
                        response.item,
                        response.requirement,
                    );
                },
            });
        }
    };

    container.querySelector("[data-compliance-show-inactive]")
        ?.addEventListener("change", async (event) => {
            const routeState = getComplianceState();
            routeState.showInactive = event.currentTarget.checked;
            routeState.focusKey = "show-inactive";
            renderComplianceBoard(container, generation);
            if (!routeState.showInactive) {return;}
            await loadComplianceSummaryPage(generation, "inactive");
            for (const itemId of routeState.expandedItems) {
                await loadComplianceRequirementPage(
                    generation,
                    itemId,
                    "inactive",
                );
            }
            renderComplianceBoard(container, generation);
        });
}

/**
 * Focuses an authorised notification target and opens the normal manual record
 * modal. Derived Service requirements remain expanded and source-read-only.
 * @param {object} params Authoritative item and requirement.
 * @param {object} params.item Tracked item.
 * @param {object} params.requirement Requirement.
 * @param {number} params.generation Current route generation.
 * @returns {Promise<object>} Target outcome.
 */
export async function openComplianceNotificationRecord({
    item,
    requirement,
    generation,
}) {
    const container = document.getElementById("compliance-board");
    if (!container || !isComplianceGenerationCurrent(generation)) {
        return {status: "navigation_not_completed"};
    }
    reconcileComplianceRequirement(item.id, requirement, item);
    const routeState = getComplianceState();
    routeState.expandedItems.add(item.id);
    routeState.expandedRequirements.add(
        focusKey([item.id, requirement.id]),
    );
    const history = await ensureRequirementHistory(
        container,
        generation,
        item,
        requirement,
    );
    if (!isComplianceGenerationCurrent(generation)) {
        return {status: "navigation_not_completed"};
    }
    if (history?.error) {
        return {status: "history_unavailable"};
    }
    if (requirement.sourceType === "maintenance_service") {
        renderComplianceBoard(container, generation);
        return {status: "source_read_only"};
    }
    const current = await loadCurrentRecordForCreate(
        container,
        generation,
        item,
        requirement,
    );
    if (!current.ready || !isComplianceGenerationCurrent(generation)) {
        return {status: "history_unavailable"};
    }
    openComplianceRecordModal({
        item,
        requirement,
        currentRecord: current.record,
        onSaved(response) {
            reconcileComplianceRequirement(
                item.id,
                response.requirement,
                response.item,
            );
            invalidateComplianceHistory(item.id, requirement.id);
            void ensureRequirementHistory(
                container,
                generation,
                response.item,
                response.requirement,
            );
        },
    });
    return {status: "modal_opened"};
}

/**
 * Renders the bounded Compliance board and binds current-generation actions.
 * @param {HTMLElement} container Board container.
 * @param {number} generation Route generation.
 * @returns {void}
 */
export function renderComplianceBoard(container, generation) {
    if (!container || !isComplianceGenerationCurrent(generation)) {return;}
    const routeState = getComplianceState();
    const items = getVisibleItems(routeState);
    container.innerHTML = `
        <div class="space-y-4" data-compliance-board-ready>
            <div class="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                <label class="inline-flex min-h-10 cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" data-compliance-show-inactive
                        data-compliance-focus-key="show-inactive"
                        ${routeState.showInactive ? "checked" : ""}>
                    <span>Show inactive</span>
                </label>
                <div class="flex flex-wrap items-center gap-2">
                    <button type="button" data-compliance-report="print"
                        class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700">
                        Print
                    </button>
                    <button type="button" data-compliance-report="csv"
                        class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700">
                        Export CSV
                    </button>
                    <button type="button" data-compliance-add-item
                        data-compliance-focus-key="add-item"
                        class="rounded-lg bg-[var(--dfc-brand-primary)] px-4 py-2 font-semibold text-[var(--dfc-brand-primary-contrast)]">
                        Add tracked item
                    </button>
                </div>
            </div>
            ${routeState.summaries.active.error ||
                (routeState.showInactive &&
                    routeState.summaries.inactive.error) ?
                renderPageError("Tracked items could not be loaded. Try again.") :
                ""}
            <div class="space-y-4">
                ${routeState.categories.map((category) =>
                    renderCategory(
                        category,
                        items.filter((item) =>
                            item.categoryId === category.id),
                        routeState,
                    ),
                ).join("")}
            </div>
            ${routeState.summaries.active.hasMore ||
                (routeState.showInactive &&
                    routeState.summaries.inactive.hasMore) ? `
                <div class="flex justify-center">
                    <button type="button" data-compliance-load-summaries
                        data-compliance-focus-key="load-summaries"
                        class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
                        ${routeState.summaries.active.loading ||
                            routeState.summaries.inactive.loading ?
                            "disabled" :
                            ""}>
                        ${routeState.summaries.active.loading ||
                            routeState.summaries.inactive.loading ?
                            "Loading..." :
                            "Load more"}
                    </button>
                </div>
            ` : ""}
        </div>
    `;
    bindBoardInteractions(container, generation);
    restoreFocus(container);
}
